import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material';

import { IDataset } from '../../models/dataset';
import { DatasetType } from '../../models/dataset-type';
import { DatasetService } from '../../services/dataset.service';
import { Latis } from '../../services/latis.service';
import { Mime, PlotsService } from '../../services/plots.service';
import { TimeRangeService } from '../../services/time-range';
import { DownloadDialogComponent } from '../download-dialog/download-dialog.component';

@Component({
    selector: 'sci-download-menu',
    templateUrl: './download-menu.component.html'
})
export class DownloadMenuComponent {
    @Input() plotId: string;
    @Input() name: string; // get the plot title from the toolbar
    // TODO: re-enable time label changes
    @Input() timeLabels = {
        timezone: '',
        format: 'auto',
        momentTimeFormat: 'YYYY-MM-DD'
    };

    constructor(
        private _dialog: MatDialog,
        private _latis: Latis,
        private _datasetService: DatasetService,
        private _rangeService: TimeRangeService,
        private _plotService: PlotsService
    ) {}

    /** Opens a dialog for multiple dataset download selection */
    openDownloadDialog() {
        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;
        const range = this._rangeService.getPlotTimeRange( this.plotId );
        const dialogRef = this._dialog.open( DownloadDialogComponent, {
            autoFocus: false,
            data: { datasets: datasets, downloadCSVFunction: this.downloadCSVforDatasets, range: range }
        });
        dialogRef.afterClosed().subscribe( ( ds: IDataset[] ) => {
            if ( ds && ds.length > 0 ) {
                this.downloadCSVforDatasets( ds );
            }
        });
    }

    /** Downloads a single dataset or opens a modal for downloading multiple datasets */
    downloadCSV() {
        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;
        if ( datasets.length === 1 ) {
            this.downloadCSVforDatasets([ datasets[0] ]);
        } else {
            this.openDownloadDialog();
        }
    }

    /**
     * Triggers a CSV download for one or more datasets at the given index or indices
     * Takes an undefined number of parameters, each one an index of a dataset to download
     */
    downloadCSVforDatasets( datasets: IDataset[] ) {
        const range = this._rangeService.getPlotTimeRange( this.plotId );
        // tell the latis factory which datasets we want to download. It will decide how they are downloaded
        let timeFormatQuery;
        if ( range.isDate ) {
            if ( this.timeLabels.format === 'secondsSinceT0' ) {
                timeFormatQuery = this._latis.timeFormatters.secondsSinceT0( new Date( range.start ) );
            } else {
                timeFormatQuery = this._latis.timeFormatters.simple( this.timeLabels.momentTimeFormat );
            }
        }
        this._latis.downloadCSV( datasets, timeFormatQuery );
    }

    /** Download image of the plot */
    downloadImage( filetype: string ) {
        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;
        if ( datasets[0].type !== DatasetType.analog && datasets[0].type !== DatasetType.discrete ) {
            console.error( 'Programmer error: downloadImage expected only for analog and discrete data' );
        }
        if ( filetype !== 'png' && filetype !== 'svg' && filetype !== 'pdf' ) {
            console.error( 'Programmer error: only png, svg, and pdf are expected for downloadImage' );
        }

        // filetype should be 'png' | 'svg' | 'pdf'
        let mime: Mime;
        if ( filetype === 'png' ) {
            mime = 'image/png';
        } else if ( filetype === 'svg' ) {
            mime = 'image/svg+xml';
        } else if ( filetype === 'pdf' ) {
            mime = 'application/pdf';
        }

        this._plotService.setDownloadChartImage( this.plotId, this.name, mime );
    }
}
